import type { LinkedCompanyAccount, ParentEntity } from "@/contexts/DetailContextProvider.types";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type { Deal } from "@/lib/types/database/deals";
import type { ContactTaskRow } from "@/lib/types/entity-rows";
import { taskLinkedEntityToParentEntity } from "@/lib/utils/crm/task-linked-entity";

/**
 * Parent entity + linked company for `UniversalDetailPage` and `UniversalDetailModalContainer`.
 * Keeps deal → `company_id` and task-linked-entity rules in one place.
 */
export function computeParentEntityForUniversalDetail<T extends BaseEntity>(
  entityName: string,
  item: T | undefined
): ParentEntity | undefined {
  if (!item) return undefined;

  if (entityName === "tasks") {
    const task = item as unknown as ContactTaskRow;
    const linked = taskLinkedEntityToParentEntity(task);
    if (linked) return linked;
    const title = typeof task.title === "string" ? task.title : undefined;
    return { type: "task", id: task.id, name: title };
  }

  if (entityName === "deals") {
    const deal = item as unknown as Deal;
    const title = typeof deal.title === "string" ? deal.title : undefined;
    return { type: "deal", id: deal.id, name: title };
  }

  const entityType =
    entityName === "contacts" ? "contact" : entityName === "companies" ? "company" : null;
  if (!entityType) return undefined;

  const name =
    (item as { name?: string; title?: string }).name ||
    (item as { title?: string }).title;
  const nameStr = typeof name === "string" ? name : undefined;

  return { type: entityType, id: item.id, name: nameStr };
}

export function computeLinkedCompanyAccountForUniversalDetail<T extends BaseEntity>(
  entityName: string,
  item: T | undefined
): LinkedCompanyAccount | null {
  if (!item || entityName !== "deals") return null;
  const deal = item as unknown as Deal;
  const cid = deal.company_id;
  if (typeof cid !== "string" || cid.length === 0) return null;

  const displayName =
    typeof deal.company_name === "string" && deal.company_name.length > 0
      ? deal.company_name
      : undefined;
  return displayName ? { id: cid, displayName } : { id: cid };
}
